import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Floating panel anchored to an element: menus, pickers, note editors.
// Becomes a bottom sheet on phones, where a floating panel next to a finger
// is hard to hit and often off-screen.
//
// mousedown is stopped at the panel so click-outside handlers on whatever is
// underneath (ProductDetailsDialog, OrderDetailsDialog) don't read a click
// inside the popover as a click outside themselves.

interface PopoverProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  align?: 'start' | 'end';
  width?: number;
  className?: string;
  label?: string;
}

const MOBILE_QUERY = '(max-width: 640px)';

const Popover: React.FC<PopoverProps> = ({ anchor, onClose, children, align = 'end', width = 260, className = '', label }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia?.(MOBILE_QUERY).matches;

  useLayoutEffect(() => {
    if (!anchor || isMobile) return;
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight || 240;
      let left = align === 'end' ? rect.right - width : rect.left;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      let top = rect.bottom + 6;
      if (top + panelHeight > window.innerHeight - 8 && rect.top - panelHeight - 6 > 8) {
        top = rect.top - panelHeight - 6;
      }
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, align, width, isMobile]);

  useEffect(() => {
    const panel = panelRef.current;
    const stop = (e: MouseEvent) => e.stopPropagation();
    panel?.addEventListener('mousedown', stop);
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (panel?.contains(target) || anchor?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    // Capture phase: runs even though the panel stops bubbling.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      panel?.removeEventListener('mousedown', stop);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <>
      {isMobile && <div className="tk-sheet-backdrop" onClick={onClose} />}
      <div
        ref={panelRef}
        role="dialog"
        aria-label={label}
        className={`tk-popover ${isMobile ? 'tk-popover-sheet' : ''} ${className}`}
        style={isMobile ? undefined : { width, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

export default Popover;
